import { useState } from "react";
import { FaChevronDown, FaChevronRight } from "@/utils/icons";
import { CallChip } from "@/components/devicePanel/CallChip";
import { Hint } from "@/components/devicePanel/Hint";
import { DeviceState } from "@/components/devicePanel/DeviceState";
import { ReviewRow, ReviewTags, useIsShown } from "@/components/devicePanel/Review";
import { tierHeading } from "@/utils/findingPriority";
import {
  STATE_TEXT,
  attributeText,
  comparedGroups,
  findingGroups,
  readingText,
  readCall,
  type ComparedFinding,
  type DevicePanelFinding,
  type DevicePanelStudyDevice,
} from "@/types/devicePanel";

/** True once the viewer has loaded the instance, so a jump to it can land. */
export type SliceLoaded = (sopInstanceUid: string) => boolean;

/** Jump the viewer to a key slice, for the finding these condition keys name. */
export type PanelJump = (sopInstanceUid: string, conditions: string[]) => void;

// Tiers whose groups open folded, their count in the heading: the routine and
// noted findings, so the acute and actionable ones lead the list.
const FOLDED_TIERS: ReadonlySet<string> = new Set(["routine_followup", "noted"]);

// Paired call words sit in fixed columns under their device names.
const PAIRED_COLUMNS = "grid-cols-[3.75rem_3.75rem_minmax(0,1fr)]";

// A group's rows sit indented under its heading, on a rule that ties them to it;
// a group without a tier and the compare columns' heading keep the inset without
// the rule, so the columns stay aligned.
const NESTED = "ml-1.5 border-l pl-1.5";

/** A device's word for the finding; empty when it does not report it. */
function WordCell({ finding, of }: { finding: DevicePanelFinding | null; of: string }) {
  const call = finding && readCall(finding);
  if (call?.kind === "hidden") throw new Error("A hidden call reached the findings list");
  return (
    <span className="flex justify-center">
      {call ? (
        <CallChip kind={call.kind} small />
      ) : (
        <span role="img" aria-label={`Not reported by ${of}`} />
      )}
    </span>
  );
}

/**
 * One finding in small muted type: name with its attributes, key slice; a call
 * below its default is dimmer. Its tooltip gives the reading. When comparing,
 * both devices' strength words lead the row, and a row where only one device
 * flags at default is shaded and says which.
 */
function FindingRow({
  row,
  owner,
  comparedTo,
  sliceLoaded,
  onJump,
}: {
  row: ComparedFinding;
  owner: DevicePanelStudyDevice;
  comparedTo: DevicePanelStudyDevice | null;
  sliceLoaded: SliceLoaded;
  onJump: PanelJump;
}) {
  const shown = (row.own ?? row.compared)!;
  const keySlice = shown.top_slices[0];
  const attributes = row.own ? attributeText(row.own) : null;
  const ownFlag = row.own?.flagged_at_default ?? false;
  const onlyOne =
    comparedTo && ownFlag !== (row.compared?.flagged_at_default ?? false)
      ? `${(ownFlag ? owner : comparedTo).short.toLowerCase()} only`
      : null;
  // The meters in words: the row's own reading, and when comparing, both.
  const hint = comparedTo
    ? [owner, comparedTo]
        .map((device, i) => {
          const finding = i === 0 ? row.own : row.compared;
          return `${device.short}: ${finding ? readingText(finding) : "not reported"}`;
        })
        .join(" / ")
    : row.own && readingText(row.own);
  const targets = [
    row.own && { device: owner, condition: row.own.condition },
    comparedTo && row.compared && { device: comparedTo, condition: row.compared.condition },
  ].filter((target) => !!target);
  return (
    <ReviewRow
      targets={targets.map(({ device, condition }) => ({ device: device.key, condition }))}
      labels={targets.map(({ device }) => device.short)}
      indent="pl-1.5"
      ready={!keySlice || sliceLoaded(keySlice)}
    >
      <Hint label={hint}>
        <button
          type="button"
          aria-disabled={!keySlice}
          onClick={() =>
            keySlice &&
            onJump(
              keySlice,
              targets.map(({ condition }) => condition),
            )
          }
          data-jump
          data-call={ownFlag ? "default" : "below-default"}
          data-only={onlyOne ?? undefined}
          className={`grid w-full items-center gap-2 rounded-xs px-1.5 py-1 text-left text-[12px] hover:bg-surface-2 focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-signal aria-disabled:cursor-default ${
            comparedTo ? PAIRED_COLUMNS : "grid-cols-[minmax(0,1fr)]"
          } ${ownFlag ? "text-fg-muted" : "text-fg-dim"} ${onlyOne ? "bg-surface-2" : ""}`}
        >
          {comparedTo && <WordCell finding={row.own} of={owner.label} />}
          {comparedTo && <WordCell finding={row.compared} of={comparedTo.label} />}
          <span>
            {shown.display_name}
            {onlyOne && (
              <span className="ml-1.5 font-mono text-[10px] text-fg-muted">{onlyOne}</span>
            )}
            {!comparedTo && row.own && readCall(row.own).kind === "incomplete" && (
              <span className="ml-1.5 font-mono text-[10px] text-fg-dim">incomplete</span>
            )}
            <ReviewTags
              targets={targets.map(({ device, condition }) => ({ device: device.key, condition }))}
            />
            {attributes && (
              <span className="ml-1.5 font-mono text-[10px] text-fg-dim">{attributes}</span>
            )}
          </span>
        </button>
      </Hint>
    </ReviewRow>
  );
}

/** Compact toggle, labelled with the compared device's short name, that overlays
 * its reading; off by default. Unavailable (with the reason in its tooltip)
 * while that device has no result. */
function CompareToggle({
  device,
  on,
  onChange,
}: {
  device: DevicePanelStudyDevice;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  const action = `Compare with ${device.label.toLowerCase()}`;
  const reason =
    device.state === "completed"
      ? null
      : [STATE_TEXT[device.state], device.message].filter(Boolean).join(": ");
  const unavailable = reason !== null;
  return (
    <Hint label={reason ? `${action} · ${reason}` : action}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={action}
        aria-disabled={unavailable}
        onClick={() => !unavailable && onChange(!on)}
        className={`rounded-xs border px-1.5 py-px font-mono text-[10.5px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-disabled:cursor-default aria-disabled:opacity-60 ${
          on
            ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
            : "border-border-strong text-fg-muted hover:text-fg"
        }`}
      >
        {device.short}
      </button>
    </Hint>
  );
}

/**
 * A findings device's list, demoted below triage: muted header, small rows,
 * grouped by clinical tier under a heading with the group's count. Acute and
 * actionable groups are open; routine and noted ones open folded. With a
 * compared device, a toggle adds both devices' words to each row and the
 * compared device's own calls as extra rows.
 */
export function FindingsSection({
  device,
  compare,
  sliceLoaded,
  onJump,
}: {
  device: DevicePanelStudyDevice;
  compare: DevicePanelStudyDevice | null;
  sliceLoaded: SliceLoaded;
  onJump: PanelJump;
}) {
  const isShown = useIsShown();
  const [comparing, setComparing] = useState(false);
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
  // Queued review items and the linked finding are always visible: compare
  // while the compared device has one.
  const compareQueued =
    !!compare &&
    compare.state === "completed" &&
    compare.findings!.some((finding) =>
      isShown({ device: compare.key, condition: finding.condition }),
    );
  const comparedOn = comparing || compareQueued;
  const comparedTo = comparedOn ? compare : null;
  const groups =
    device.state !== "completed"
      ? null
      : comparedTo
        ? comparedGroups(device, comparedTo, (finding) =>
            isShown({ device: comparedTo.key, condition: finding.condition }),
          )
        : findingGroups(device).map(({ category, rows }) => ({
            category,
            rows: rows.map((own) => ({ condition: own.condition, own, compared: null })),
          }));
  const rowShown = (row: ComparedFinding) =>
    (!!row.own && isShown({ device: device.key, condition: row.own.condition })) ||
    (!!comparedTo &&
      !!row.compared &&
      isShown({ device: comparedTo.key, condition: row.compared.condition }));
  const total = groups?.reduce((sum, group) => sum + group.rows.length, 0) ?? 0;
  const unfold = (tier: string) =>
    setUnfolded((current) => {
      const next = new Set(current);
      if (!next.delete(tier)) next.add(tier);
      return next;
    });
  return (
    <section
      aria-label={`${device.short} findings`}
      className="flex min-h-0 flex-1 flex-col border-t border-border-strong bg-bg pt-4"
    >
      {/* A label, not a row: its own register so the demoted rows below never read as a header. */}
      <div className="flex shrink-0 items-center gap-1.5 px-3 pb-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-fg-dim">
        <h2 className="font-normal">{device.short}</h2>
        <span aria-hidden>·</span>
        <span>{device.status_label}</span>
        {compare && device.state === "completed" && (
          <span className="ml-auto normal-case tracking-normal">
            <CompareToggle device={compare} on={comparedOn} onChange={setComparing} />
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {device.state !== "completed" ? (
          <p className="px-3 py-2">
            <DeviceState state={device.state} message={device.message} className="text-[11px]" />
          </p>
        ) : total === 0 ? (
          <p className="px-3 py-2 text-[11px] text-fg-dim">Nothing flagged</p>
        ) : (
          <div className="grid gap-px px-2 pb-3 pt-1">
            {comparedTo && (
              <div
                aria-hidden
                className={`grid ${PAIRED_COLUMNS} ${NESTED} gap-2 border-transparent px-1.5 pt-1 text-center font-mono text-[9.5px] uppercase tracking-[0.06em] text-fg-dim`}
              >
                <span>{device.short}</span>
                <span>{comparedTo.short}</span>
              </div>
            )}
            {(groups ?? []).map(({ category, rows }) => {
              const tier = category ?? "";
              const heading = tierHeading(category);
              const folds = FOLDED_TIERS.has(tier);
              // Queued review items and the linked finding keep their group open.
              const held = rows.some(rowShown);
              const open = !folds || held || unfolded.has(tier);
              return (
                <section
                  key={tier}
                  data-testid="finding-group"
                  aria-label={heading || undefined}
                  className="border-t border-border pt-1 first:border-t-0 first:pt-0"
                >
                  {heading && (
                    <h3 className="px-1.5 pb-1 pt-2 text-[10px] font-normal uppercase tracking-[0.08em] text-fg-muted">
                      {folds ? (
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-disabled={held}
                          onClick={() => !held && unfold(tier)}
                          className="inline-flex items-center gap-1.5 uppercase tracking-[0.08em] hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-disabled:cursor-default"
                        >
                          {heading} · {rows.length}
                          {open ? (
                            <FaChevronDown aria-hidden className="h-2 w-2" />
                          ) : (
                            <FaChevronRight aria-hidden className="h-2 w-2" />
                          )}
                        </button>
                      ) : (
                        <>
                          {heading} · {rows.length}
                        </>
                      )}
                    </h3>
                  )}
                  {open && (
                    <div
                      className={`${NESTED} ${heading ? "border-border" : "border-transparent"}`}
                    >
                      {rows.map((row) => (
                        <FindingRow
                          key={row.condition}
                          row={row}
                          owner={device}
                          comparedTo={comparedTo}
                          sliceLoaded={sliceLoaded}
                          onJump={onJump}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
